<template>
  <Loading class="" v-if="isPropertyLoading || isResourcesLoading" />
  <div v-else>
    <BethsLayout :is-wrapped="hideLayoutWrapper" :layoutTitle="`Resources - ${property?.name}`">
      <div class="mb-28">
        <div v-if="!hideLayoutWrapper" class="text-3xl font-bold mb-4">Resources</div>
        <div v-if="numbers?.length === 0 && documents?.length === 0 && linkSections?.length === 0">
          No resources found.
        </div>
        <div v-else>
          <div v-if="documents.length > 0">
            <div class="mt-0 mb-8 lg:mb-12 col-span-0.5">
              <div class="flex">
                <div class="whitespace-nowrap font-bold">Documents</div>
                <div class="flex flex-justify-between items-center w-full pl-5">
                  <img src="\images\Ellipse.png" alt="" />
                  <div class="bg-divider mt-0.5 h-0.5 w-full"></div>
                  <img src="\images\Ellipse.png" alt="" />
                </div>
              </div>
            </div>

            <div
              class="text-sm text-link font-normal whitespace-nowrap ml-0 lg:ml-5 xl:ml-0 flex flex-wrap"
            >
              <div
                v-for="doc in documents"
                :key="doc.documentId"
                class="mb-2.5 xl:basis-1/4 lg:basis-1/3 md:basis-1/2 basis-full"
              >
                <a
                  :href="
                    (userStore.isLoggedIn &&
                      doc.siteRoles.some((d) => d.siteRoleId == userStore.user.siteRoleId)) ||
                    doc.allowAnonDownload
                      ? userStore.isEmailVerifiedOrNotRequired || doc.allowAnonDownload
                        ? `/api/Property/document/${doc.documentId}/download/${doc.fileName}?token=${user?.token}`
                        : `/${bldgStore.siteCode}/unconfirmedemail?returnUrl=${router.currentRoute.value.path}`
                      : `/${bldgStore.siteCode}/login?returnUrl=${router.currentRoute.value.path}`
                  "
                  :target="
                    (userStore.isLoggedIn &&
                      userStore.isEmailVerifiedOrNotRequired &&
                      doc.siteRoles.some((d) => d.siteRoleId == userStore.user.siteRoleId)) ||
                    doc.allowAnonDownload
                      ? '_blank'
                      : ''
                  "
                  class="pl-5"
                >
                  {{ doc.label }}
                </a>
              </div>
            </div>
          </div>

          <div v-if="linkSections.length > 0">
            <div class="mt-12 mb-12 col-span-0.5">
              <div class="flex">
                <div class="whitespace-nowrap font-bold">Links</div>
                <div class="flex flex-justify-between items-center w-full pl-5">
                  <img src="\images\Ellipse.png" alt="" />
                  <div class="bg-divider mt-0.5 h-0.5 w-full"></div>
                  <img src="\images\Ellipse.png" alt="" />
                </div>
              </div>
            </div>

            <div class="font-normal ml-0 lg:ml-5 xl:ml-0 flex flex-wrap">
              <div
                v-for="(linkSection, i) in linkSections"
                :key="i"
                class="text-base pb-7 pl-5 lg:mb-4 xl:basis-1/4 lg:basis-1/3 md:basis-1/2 basis-full"
              >
                <div class="font-bold">{{ linkSection.subheading }}</div>
                <a
                  v-for="link in linkSection.links"
                  :key="link.linkId"
                  :href="link.linkUrl"
                  target="_blank"
                  class="text-smDocs mb-1 block text-link"
                >
                  {{ link.label }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BethsLayout>
  </div>
</template>

<script setup lang="ts">
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";

const bldgStore = useBldgStore();
const userStore = useUserStore();
const router = useRouter();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var isResourcesLoading = ref(true);
var isUserLoadedFromServer = computed(() => userStore.isUserLoadedFromServer);
var user = computed(() => userStore.user);
//const route = useRoute();

withDefaults(
  defineProps<{
    hideLayoutWrapper?: boolean;
  }>(),
  {
    hideLayoutWrapper: false,
  }
);

var numbers = computed(() => {
  let numbers = bldgStore.property.numbers;
  return userStore.isLoggedIn && userStore.isEmailVerifiedOrNotRequired
    ? numbers.filter((n) => n.siteRoles.find((r) => r.siteRoleId == userStore.user.siteRoleId))
    : numbers.filter((n) => n.allowAnonView);
});

var documents = computed(() => {
  let docs = bldgStore.property.documents;
  return userStore.isLoggedIn && userStore.isEmailVerifiedOrNotRequired
    ? docs.filter(
        (d) =>
          d.siteRoles.find((r) => r.siteRoleId == userStore.user.siteRoleId) || d.allowAnonLinkView
      )
    : docs.filter((d) => d.allowAnonLinkView);
});

var linkSections = computed(() => bldgStore.linkSections);

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isResourcesLoading.value = true;
  if (bldgStore.property?.portalId) {
    redirectIfNotLoggedIn();
    await bldgStore.getLinks(bldgStore.property?.portalId);
  }
  isResourcesLoading.value = false;
}

watch(isUserLoadedFromServer, redirectIfNotLoggedIn);
watch(user, redirectIfNotLoggedIn);
function redirectIfNotLoggedIn() {
  let currentRoute = router.currentRoute.value.path;

  // For portals with hideAllAnonymousContent = true:
  // If user loading attempt is complete and we're still not logged in, redirect to login page
  if (
    isUserLoadedFromServer.value &&
    property.value &&
    property.value.hideAllAnonymousContent &&
    !userStore.isLoggedIn
  ) {
    console.log(`Redirecting to login page (isLoggedIn = ${userStore.isLoggedIn})`);
    router.push(`/${property.value.siteCode}/login?returnUrl=${currentRoute}`);
  }

  if (
    isUserLoadedFromServer.value &&
    property.value &&
    property.value.hideAllAnonymousContent &&
    !userStore.isEmailVerifiedOrNotRequired
  ) {
    console.log(
      `Redirecting to unconfirmed email page (isEmailVerifiedOrNotRequired = ${userStore.isEmailVerifiedOrNotRequired})`
    );
    router.push(`/${property.value.siteCode}/unconfirmedemail?returnUrl=${currentRoute}`);
  }
}
</script>
